import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { professionalClientDashboardService } from '../../../services/professionalClientDashboardService';
import { professionalClientNotificationService } from '../../../services/professionalClientNotificationService';
import { supabase } from '../../../lib/supabase';

const VueEnsemble = ({ professionalClient }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [lastOrder, setLastOrder] = useState(null);

  useEffect(() => {
    loadDashboardData();
    loadNotifications();
    loadLastOrder();

    // Setup real-time subscriptions
    if (!professionalClient?.email) return;

    console.log('Setting up real-time subscriptions for:', professionalClient?.email);

    // Subscribe to shipment updates
    const shipmentSubscription = professionalClientDashboardService?.subscribeToShipmentUpdates(
      professionalClient?.email,
      (update) => {
        console.log('Real-time update received:', update);
        // Reload dashboard data when shipments or events change
        loadDashboardData();
      }
    );

    // Subscribe to KPI updates (barrel sales, deposits, containers)
    const kpiSubscription = professionalClientDashboardService?.subscribeToKPIUpdates(
      professionalClient?.email,
      (update) => {
        console.log('Real-time KPI update received:', update);
        // Reload dashboard data when KPIs change
        loadDashboardData();
      }
    );

    // Subscribe to real-time notification updates
    const notificationSubscription = professionalClientNotificationService?.subscribeToNotifications(
      (update) => {
        console.log('Real-time notification update:', update);
        if (update?.eventType === 'INSERT') {
          // New notification received
          loadNotifications();
        } else if (update?.eventType === 'UPDATE') {
          // Notification updated (e.g., marked as read)
          loadNotifications();
        }
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      console.log('Cleaning up real-time subscriptions');
      shipmentSubscription?.unsubscribe();
      kpiSubscription?.unsubscribe();
      notificationSubscription?.unsubscribe();
    };
  }, [professionalClient?.email]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await professionalClientDashboardService?.getDashboardData();
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const [notificationsData, unreadCount] = await Promise.all([
        professionalClientNotificationService?.getNotifications({ page: 1, limit: 10 }),
        professionalClientNotificationService?.getUnreadCount()
      ]);
      setNotifications(notificationsData?.notifications || []);
      setUnreadNotificationsCount(unreadCount);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const loadLastOrder = async () => {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { data: client } = await supabase
        ?.from('professional_clients')
        ?.select('id')
        ?.eq('auth_user_id', user?.id)
        ?.maybeSingle();

      if (!client?.id) return;

      const { data } = await supabase
        ?.from('pro_pending_orders')
        ?.select('*')
        ?.eq('professional_client_id', client?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();

      setLastOrder(data || null);
    } catch (err) {
      console.error('Error loading last order:', err);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await professionalClientNotificationService?.markAsRead(notificationId);
      loadNotifications();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'shipment_status':
        return 'Package';
      case 'invoice_ready':
        return 'FileText';
      case 'certificate_ready':
        return 'Award';
      case 'container_departure':
        return 'Ship';
      case 'promotion':
        return 'Tag';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'shipment_status':
        return 'blue';
      case 'invoice_ready':
        return 'orange';
      case 'certificate_ready':
        return 'green';
      case 'container_departure':
        return 'purple';
      case 'promotion':
        return 'pink';
      default:
        return 'gray';
    }
  };

  const getOrderTypeLabel = (type) => {
    const labels = {
      order: 'Commande articles',
      quote_volume: 'Envoi au volume',
      quote_barrels: 'Barriques',
      quote_container: 'Conteneur',
    };
    return labels?.[type] || type;
  };

  const getOrderStatusBadge = (status) => {
    const badges = {
      new: { label: 'Nouvelle', color: 'bg-blue-100 text-blue-800' },
      processing: { label: 'En traitement', color: 'bg-yellow-100 text-yellow-800' },
      quote_sent: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-800' },
      confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'Refusée', color: 'bg-red-100 text-red-800' },
    };
    const badge = badges?.[status] || badges?.new;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="AlertCircle" size={24} className="text-red-600" />
          <h3 className="text-lg font-semibold text-red-900">Erreur</h3>
        </div>
        <p className="text-red-700 mb-4">{error}</p>
        <Button onClick={loadDashboardData} variant="outline">
          Réessayer
        </Button>
      </div>
    );
  }

  const { kpis, expeditionsEnCours, prochainDepart } = dashboardData || {};

  return (
    <div className="space-y-6">
      {/* Premium Banner */}
      <div className="bg-gradient-to-r from-green-800 to-green-900 rounded-xl p-6 text-white shadow-lg border border-green-700">
        <div className="flex items-center space-x-3 mb-3">
          <Icon name="Shield" size={28} className="text-green-300" />
          <h2 className="text-2xl font-bold">Plateforme logistique digitale sécurisée</h2>
        </div>
        <p className="text-green-100 text-lg">
          Gestion centralisée • Traçabilité complète • Pilotage simplifié
        </p>
      </div>

      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">
          Bienvenue dans votre centre de pilotage logistique
        </h1>
        <p className="text-green-50 text-lg">
          Corridor France – Côte d'Ivoire • Suivi en temps réel
        </p>
        <p className="text-green-50 text-sm mt-2">
          {format(new Date(), 'EEEE d MMMM yyyy', { locale: fr })}
        </p>
      </div>

      {/* Last Order Card */}
      <div className="bg-white rounded-lg shadow-md border border-green-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="ShoppingCart" size={22} className="text-green-600" />
            <h2 className="text-lg font-bold text-gray-900">Dernière commande</h2>
          </div>
          <button
            onClick={() => navigate('/professional-dashboard/commander')}
            className="text-sm text-green-600 hover:text-green-700 font-medium"
          >
            Voir tout l'historique →
          </button>
        </div>
        {lastOrder ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Type</p>
                <p className="text-sm font-semibold text-gray-900">{getOrderTypeLabel(lastOrder?.order_type)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Détails</p>
                <p className="text-sm text-gray-700 truncate">{lastOrder?.volume_quantity || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Date</p>
                <p className="text-sm text-gray-700">
                  {lastOrder?.created_at ? format(new Date(lastOrder?.created_at), 'd MMM yyyy', { locale: fr }) : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-medium mb-1">Statut</p>
                {getOrderStatusBadge(lastOrder?.status)}
              </div>
            </div>
            {lastOrder?.estimated_amount && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Montant estimé : <span className="font-bold text-gray-900">{parseFloat(lastOrder?.estimated_amount)?.toFixed(0)} €</span>
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-3">
              <Icon name="ShoppingCart" size={24} className="text-green-400" />
            </div>
            <p className="text-gray-500 text-sm">Aucune commande pour le moment</p>
            <button
              onClick={() => navigate('/professional-dashboard/commander')}
              className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium underline"
            >
              Passer votre première commande
            </button>
          </div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingUp" size={24} className="text-green-600" />
            </div>
            {kpis?.expeditionsEnCours > 0 && (
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            )}
          </div>
          <p className="text-gray-600 text-sm mb-1">Flux actifs</p>
          <p className="text-3xl font-bold text-gray-900">{kpis?.expeditionsEnCours || 0}</p>
          <p className="text-xs text-green-600 mt-2 flex items-center">
            <Icon name="Activity" size={12} className="mr-1" />
            Statut opérationnel
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Package" size={24} className="text-blue-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Volume traité 2026</p>
          <p className="text-3xl font-bold text-gray-900">{kpis?.totalExpedieCetteAnnee || 0}</p>
          <p className="text-xs text-blue-600 mt-2 flex items-center">
            <Icon name="TrendingUp" size={12} className="mr-1" />
            Évolution mensuelle
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Icon name="FileText" size={24} className="text-orange-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Facturation à venir</p>
          <p className="text-2xl font-bold text-gray-900">{kpis?.prochaineFacture?.montant || 0} €</p>
          {kpis?.prochaineFacture?.dateEcheance && (
            <p className="text-xs text-gray-500 mt-1">
              Échéance: {format(new Date(kpis?.prochaineFacture?.dateEcheance), 'd MMM yyyy', { locale: fr })}
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="TrendingDown" size={24} className="text-purple-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Optimisation tarifaire</p>
          <p className="text-3xl font-bold text-green-600">{kpis?.economiesRealisees || 0} €</p>
          <p className="text-xs text-purple-600 mt-2 flex items-center">
            <Icon name="Percent" size={12} className="mr-1" />
            Variation %
          </p>
        </div>
      </div>
      {/* Real-time Shipment Tracking */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center space-x-2">
          <Icon name="MapPin" size={24} className="text-green-600" />
          <span>Centre de tracking opérationnel</span>
        </h2>
        <p className="text-gray-600 text-sm mb-6">
          Visualisation en direct des flux maritimes Paris – Abidjan.
        </p>

        <div className="space-y-6">
          {expeditionsEnCours?.map((expedition) => (
            <div key={expedition?.reference} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex flex-wrap items-center justify-between mb-4">
                <div>
                  <p className="font-bold text-gray-900 text-lg">{expedition?.reference}</p>
                  <p className="text-sm text-gray-600">
                    {expedition?.type} • Départ: {format(new Date(expedition?.dateDepart), 'd MMM yyyy', { locale: fr })} • Navire: {expedition?.navire}
                  </p>
                </div>
                <Button size="sm" variant="outline">
                  Voir les détails
                </Button>
              </div>

              {/* Progress Bar */}
              <div className="relative">
                <div className="flex justify-between items-start">
                  {expedition?.etapes?.map((etape, index) => (
                    <div key={index} className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                          etape?.completed
                            ? 'bg-green-500 border-green-500'
                            : etape?.active
                            ? 'bg-blue-500 border-blue-500 animate-pulse' :'bg-gray-200 border-gray-300'
                        }`}
                      >
                        {etape?.completed ? (
                          <Icon name="Check" size={20} className="text-white" />
                        ) : etape?.active ? (
                          <Icon name="Loader" size={20} className="text-white" />
                        ) : (
                          <span className="text-gray-500 text-xs">{index + 1}</span>
                        )}
                      </div>
                      <p className="text-xs text-center mt-2 text-gray-700 font-medium">{etape?.label}</p>
                      <p className="text-xs text-center text-gray-500 mt-1">{etape?.date}</p>
                    </div>
                  ))}
                </div>
                <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-300 -z-10"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notifications History */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Icon name="Bell" size={24} className="text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
              {unreadNotificationsCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  {unreadNotificationsCount}
                </span>
              )}
            </div>
            <button
              onClick={loadNotifications}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Actualiser
            </button>
          </div>

          {loadingNotifications ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : notifications?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="Bell" size={48} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune notification</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {notifications?.map((notif) => {
                const iconName = getNotificationIcon(notif?.notificationType);
                const color = getNotificationColor(notif?.notificationType);
                const bgColor = notif?.isRead ? 'bg-gray-50' : 'bg-blue-50';
                const borderColor = notif?.isRead ? 'border-gray-200' : 'border-blue-200';

                return (
                  <div
                    key={notif?.id}
                    className={`p-4 rounded-lg border ${bgColor} ${borderColor} hover:shadow-md transition-shadow cursor-pointer`}
                    onClick={() => !notif?.isRead && handleMarkAsRead(notif?.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <Icon name={iconName} size={20} className={`text-${color}-600`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {notif?.subject}
                          </p>
                          {!notif?.isRead && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 ml-2"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{notif?.message}</p>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-gray-500">
                            {new Date(notif?.createdAt)?.toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                          <div className="flex items-center space-x-2">
                            {notif?.channel === 'email' && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                <Icon name="Mail" size={12} className="inline mr-1" />
                                Email
                              </span>
                            )}
                            {notif?.channel === 'sms' && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                <Icon name="Smartphone" size={12} className="inline mr-1" />
                                SMS
                              </span>
                            )}
                            {notif?.status === 'sent' && (
                              <Icon name="Check" size={14} className="text-green-600" />
                            )}
                            {notif?.status === 'failed' && (
                              <Icon name="AlertCircle" size={14} className="text-red-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {notifications?.length > 0 && (
            <div className="mt-4 text-center">
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>

        {/* Next Departure */}
        <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg shadow-md p-6 text-white">
          <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
            <Icon name="Ship" size={24} />
            <span>Prochaine fenêtre logistique</span>
          </h2>
          {prochainDepart?.date && !isNaN(new Date(prochainDepart.date)?.getTime()) ? (
            <>
              <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
                <p className="text-3xl font-bold mb-2">{format(new Date(prochainDepart.date), 'd MMMM yyyy', { locale: fr })}</p>
                <p className="text-lg mb-3">
                  <span className="font-bold text-2xl">{prochainDepart?.joursRestants}</span> jours restants pour déposer vos marchandises
                </p>
                <div className="border-t border-white/30 pt-3 mt-3 space-y-2">
                  <p className="text-sm flex items-center">
                    <Icon name="Calendar" size={16} className="mr-2" />
                    <span className="font-semibold">Date limite de réception entrepôt France:</span>
                    <span className="ml-2">{format(new Date(prochainDepart.date), 'd MMM yyyy', { locale: fr })}</span>
                  </p>
                  <p className="text-sm flex items-center">
                    <Icon name="Anchor" size={16} className="mr-2" />
                    <span className="font-semibold">Départ maritime estimé:</span>
                    <span className="ml-2">{format(new Date(new Date(prochainDepart.date).getTime() + 7 * 24 * 60 * 60 * 1000), 'd MMM yyyy', { locale: fr })}</span>
                  </p>
                </div>
              </div>
              <Button 
                className="w-full bg-white text-green-700 hover:bg-gray-100 font-semibold"
                onClick={() => navigate('/professional-dashboard/commander')}
              >
                Réserver ma place
              </Button>
            </>
          ) : (
            <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
              <p className="text-lg">Aucun départ prévu pour le moment</p>
              <p className="text-sm mt-2 opacity-90">Nous vous informerons dès qu'une nouvelle date sera disponible</p>
            </div>
          )}
        </div>
      </div>

      {/* Documents Section */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <div className="mb-4">
          <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Gestion documentaire centralisée</p>
          <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
            <Icon name="FileText" size={24} className="text-blue-600" />
            <span>Mes Documents</span>
          </h2>
        </div>
        <p className="text-gray-600 mb-6">
          Tous vos certificats EX1, factures et preuves d'export sont archivés et accessibles 24/24.
        </p>
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => navigate('/professional-client-dashboard-v2/documents')}
        >
          <Icon name="FolderOpen" size={20} className="mr-2" />
          Accéder à mes documents
        </Button>
      </div>

      {/* Professional Advisor Section */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-md p-6 text-white">
        <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
          <Icon name="Headphones" size={24} />
          <span>Votre gestionnaire logistique dédié</span>
        </h2>
        <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-4 mb-3">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
              <Icon name="User" size={32} className="text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-lg">{professionalClient?.advisorName || 'Équipe ADT Express'}</p>
              <p className="text-sm text-blue-100">Gestionnaire logistique</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <p className="flex items-center">
              <Icon name="CheckCircle" size={16} className="mr-2" />
              Contact direct sans intermédiaire
            </p>
            <p className="flex items-center">
              <Icon name="CheckCircle" size={16} className="mr-2" />
              Assistance export France – Côte d'Ivoire
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Button 
            className="bg-white text-blue-700 hover:bg-gray-100 font-semibold"
            onClick={() => window.location.href = 'tel:0651988621'}
          >
            <Icon name="Phone" size={18} className="mr-2" />
            Appeler
          </Button>
          <Button 
            className="bg-white text-blue-700 hover:bg-gray-100 font-semibold"
            onClick={() => window.location.href = 'mailto:contact@adtexpress.fr'}
          >
            <Icon name="Mail" size={18} className="mr-2" />
            Email
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VueEnsemble;